<script setup>
// ReceiptDraftSendButton — "Send to customer" action rendered in the meta row
// of a DRAFT-receipt private note (posted by the store panel's Receipt popup).
// Clicking re-posts the EXACT previewed PDF as a customer-visible outgoing
// message via the owning company's backend (sapi for -MK inboxes, sapi3 for
// -S3 — same suffix convention as StorePanelContainer / ChatList company views).
import { computed, ref } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from './provider.js';

const { id: messageId, inboxId, content, isPrivate } = useMessageContext();

const isDraftReceipt = computed(
  () => !!isPrivate?.value && /DRAFT receipt /.test(content?.value || '')
);

const getInboxById = useMapGetter('inboxes/getInbox');

const backendUrl = computed(() => {
  const inbox = getInboxById.value(inboxId?.value);
  const isS3 = /[-\s]S3$/i.test((inbox?.name || '').trim());
  if (window.location.hostname === 'localhost') {
    return isS3 ? 'http://localhost:3669' : 'http://localhost:3668';
  }
  return isS3 ? 'https://sapi3.mkhb.my' : 'https://sapi.mkhb.my';
});

const accountId = computed(() => {
  const m = window.location.pathname.match(/accounts\/(\d+)/);
  return m ? m[1] : '';
});
const conversationId = computed(() => {
  const m = window.location.pathname.match(/conversations\/(\d+)/);
  return m ? m[1] : '';
});

const state = ref('idle'); // idle | sending | sent | error

async function sendToCustomer() {
  if (state.value === 'sending' || state.value === 'sent') return;
  state.value = 'sending';
  try {
    const resp = await fetch(
      `${backendUrl.value}/api/store-dashboard/send-draft-receipt`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: conversationId.value,
          accountId: Number(accountId.value) || undefined,
          messageId: messageId?.value,
        }),
      }
    );
    const json = await resp.json();
    if (!json.success) throw new Error(json.error || 'Failed');
    state.value = 'sent';
  } catch (e) {
    state.value = 'error';
    setTimeout(() => {
      state.value = 'idle';
    }, 3000);
  }
}

const icon = computed(() => {
  if (state.value === 'sending') return 'i-lucide-loader-circle';
  if (state.value === 'sent') return 'i-lucide-check';
  if (state.value === 'error') return 'i-lucide-refresh-cw';
  return 'i-lucide-send';
});

const title = computed(() => {
  if (state.value === 'sending') return 'Sending…';
  if (state.value === 'sent') return 'Sent to customer';
  if (state.value === 'error') return 'Failed — tap to retry';
  return 'Send to customer';
});
</script>

<template>
  <button
    v-if="isDraftReceipt"
    v-tooltip.top="title"
    class="flex size-6 items-center justify-center rounded-full text-white transition-colors"
    :class="{
      'bg-n-teal-9 hover:bg-n-teal-10': state === 'idle',
      'bg-n-slate-9 cursor-wait': state === 'sending',
      'bg-n-teal-10 cursor-default': state === 'sent',
      'bg-n-ruby-9 hover:bg-n-ruby-10': state === 'error',
    }"
    :disabled="state === 'sending' || state === 'sent'"
    :aria-label="title"
    @click.stop="sendToCustomer"
  >
    <Icon
      :icon="icon"
      class="size-3.5"
      :class="{ 'animate-spin': state === 'sending' }"
    />
  </button>
</template>
